import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { CheckCircle2, Trophy, Share2, Copy, MessageCircle, Mail, ArrowRight } from "lucide-react";

export default function CompetitionPage() {
  const { slug } = useParams();
  const [comp, setComp] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [thankYou, setThankYou] = useState("");
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    marketing_opt_in: false, custom_answer: "",
    accepted_terms: false,
  });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/public/competitions/${slug}`).catch((err) => { throw err; }),
      axios.get(`${API}/settings?lite=true`).catch(() => ({ data: {} })),
    ])
      .then(([compRes, setRes]) => {
        setComp(compRes.data);
        setSettings(setRes.data || {});
      })
      .catch(() => {
        setComp(null);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const salonName = settings.salon_name || "";
  const salonLogo = settings.icon_logo_url || settings.logo_url || "";

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!comp) return;
    const cfg = comp.field_config || {};
    // Required-field guard (client-side)
    const checks = [
      ["name", form.name?.trim()],
      ["email", form.email?.trim()],
      ["phone", form.phone?.trim()],
    ];
    for (const [k, v] of checks) {
      if (cfg[k]?.visible && cfg[k]?.required && !v) {
        toast.error(`${cfg[k]?.label || k} is required`);
        return;
      }
    }
    if (cfg.custom_question?.visible && cfg.custom_question?.required && !form.custom_answer?.trim()) {
      toast.error(`${cfg.custom_question.label} is required`);
      return;
    }
    if (!form.accepted_terms) {
      toast.error("Please accept the terms");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public/competitions/${slug}/enter`, form);
      setThankYou(res.data?.thank_you_message || comp.thank_you_message || "Thanks for entering!");
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    }
    setSubmitting(false);
  };

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = encodeURIComponent(`Enter ${comp?.name || "this competition"} ✨ ${shareUrl}`);
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied!"); }
    catch { toast.error("Couldn't copy"); }
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: comp?.name, url: shareUrl }); } catch { /* cancelled */ }
    } else { copyLink(); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-background"><p className="text-sm text-muted-foreground">Loading…</p></div>;
  }
  if (!comp) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="competition-not-found">
        <Card className="p-8 max-w-md text-center">
          <Trophy size={36} className="mx-auto text-muted-foreground/40 mb-3" />
          <h1 className="text-xl font-semibold">Competition not found</h1>
          <p className="text-sm text-muted-foreground mt-2">This competition doesn't exist or is no longer accepting entries.</p>
          <Link to="/"><Button variant="outline" className="rounded-xl mt-4">← Back home</Button></Link>
        </Card>
      </div>
    );
  }

  const cfg = comp.field_config || {};
  const isClosed = comp.status !== "active";

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-background to-purple-50" data-testid="competition-public-page">
      <Toaster position="top-center" />

      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2">
            {salonLogo ? (
              <img src={salonLogo} alt={salonName || "Salon"} className="w-8 h-8 rounded-lg object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                {((salonName || "B")[0]).toUpperCase()}
              </div>
            )}
            <span className="font-semibold text-sm" style={{ fontFamily: "Playfair Display" }}>{salonName}</span>
          </Link>
          <Button size="sm" variant="ghost" className="rounded-xl" onClick={nativeShare} data-testid="public-share-btn">
            <Share2 size={14} className="mr-1.5" /> Share
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Banner */}
        {comp.banner_image_url && (
          <div className="rounded-2xl overflow-hidden shadow-md">
            <img src={comp.banner_image_url} alt={comp.name} className="w-full max-h-72 object-cover" />
          </div>
        )}

        {/* Title */}
        <div className="text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-medium mb-3">
            <Trophy size={12} /> Competition
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ fontFamily: "Playfair Display" }} data-testid="competition-title">
            {comp.name}
          </h1>
          {comp.prize && (
            <p className="text-base text-foreground/80 mt-2 font-medium" data-testid="competition-prize">
              Win: <span className="text-primary">{comp.prize}</span>
            </p>
          )}
        </div>

        {/* Description */}
        {comp.description && (
          <Card className="p-5">
            <p className="text-sm text-foreground/80 whitespace-pre-wrap leading-relaxed" data-testid="competition-description">{comp.description}</p>
          </Card>
        )}

        {/* Form / Thank-you */}
        {submitted ? (
          <Card className="p-8 text-center space-y-4" data-testid="competition-thanks">
            <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto flex items-center justify-center">
              <CheckCircle2 size={28} className="text-emerald-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">You're in! 🎉</h2>
              <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{thankYou}</p>
            </div>
            <div className="border-t pt-4">
              <p className="text-xs text-muted-foreground mb-2">Help us spread the word</p>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <a href={`https://wa.me/?text=${shareText}`} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="rounded-xl" data-testid="thanks-share-whatsapp">
                    <MessageCircle size={14} className="mr-1.5" /> WhatsApp
                  </Button>
                </a>
                <a href={`mailto:?subject=${encodeURIComponent(`Enter ${comp.name}`)}&body=${shareText}`}>
                  <Button variant="outline" size="sm" className="rounded-xl" data-testid="thanks-share-email">
                    <Mail size={14} className="mr-1.5" /> Email
                  </Button>
                </a>
                <Button variant="outline" size="sm" className="rounded-xl" onClick={copyLink} data-testid="thanks-share-copy">
                  <Copy size={14} className="mr-1.5" /> Copy link
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <Card className="p-5 sm:p-6">
            {isClosed ? (
              <div className="text-center py-6" data-testid="competition-closed">
                <p className="text-sm font-medium">This competition isn't accepting entries.</p>
                <p className="text-xs text-muted-foreground mt-1">Check back soon!</p>
              </div>
            ) : (
              <form className="space-y-4" onSubmit={submit} data-testid="competition-form">
                <h2 className="text-lg font-semibold">Enter the competition</h2>

                {cfg.name?.visible && (
                  <div>
                    <Label>{cfg.name.label}{cfg.name.required ? " *" : ""}</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required={cfg.name.required}
                      className="mt-1"
                      data-testid="public-field-name"
                    />
                  </div>
                )}
                {cfg.email?.visible && (
                  <div>
                    <Label>{cfg.email.label}{cfg.email.required ? " *" : ""}</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required={cfg.email.required}
                      className="mt-1"
                      data-testid="public-field-email"
                    />
                  </div>
                )}
                {cfg.phone?.visible && (
                  <div>
                    <Label>{cfg.phone.label}{cfg.phone.required ? " *" : ""}</Label>
                    <Input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      required={cfg.phone.required}
                      className="mt-1"
                      data-testid="public-field-phone"
                    />
                  </div>
                )}
                {cfg.custom_question?.visible && (
                  <div>
                    <Label>{cfg.custom_question.label}{cfg.custom_question.required ? " *" : ""}</Label>
                    <Textarea
                      value={form.custom_answer}
                      onChange={(e) => setForm({ ...form, custom_answer: e.target.value })}
                      required={cfg.custom_question.required}
                      rows={3}
                      className="mt-1"
                      data-testid="public-field-custom"
                    />
                  </div>
                )}
                {cfg.marketing_opt_in?.visible && (
                  <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="public-field-optin-label">
                    <Checkbox
                      checked={!!form.marketing_opt_in}
                      onCheckedChange={(v) => setForm({ ...form, marketing_opt_in: !!v })}
                      data-testid="public-field-optin"
                    />
                    <span className="text-foreground/80">{cfg.marketing_opt_in.label}</span>
                  </label>
                )}
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="public-field-terms-label">
                  <Checkbox
                    checked={!!form.accepted_terms}
                    onCheckedChange={(v) => setForm({ ...form, accepted_terms: !!v })}
                    required
                    data-testid="public-field-terms"
                  />
                  <span className="text-foreground/80">{comp.terms_label || "I accept the terms & conditions"} *</span>
                </label>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-xl mt-2"
                  size="lg"
                  data-testid="public-submit-btn"
                >
                  {submitting ? "Submitting…" : (<>Enter Competition <ArrowRight size={16} className="ml-1.5" /></>)}
                </Button>
              </form>
            )}
          </Card>
        )}

        <p className="text-center text-[11px] text-muted-foreground pt-4">
          Powered by B∞kd
        </p>
      </main>
    </div>
  );
}
